import { useEffect, useState } from "react";
import type { DeviceMediaItem } from "@workspace/api-client-react";

/** How long the incoming image fades and the outgoing item stays mounted. */
const FADE_MS = 300;
/** Extra hold on the outgoing layer so the fade finishes before it unmounts. */
const OUTGOING_LINGER_MS = 120;

interface Layers {
  current: DeviceMediaItem;
  /** Item kept mounted underneath while the incoming image fades in. */
  outgoing: DeviceMediaItem | null;
  /** Whether `current` arrived via a fade (incoming image over prior media). */
  fade: boolean;
}

/**
 * Signage surface for the video channel: still images crossfade in over the
 * outgoing item (which stays mounted ~300ms underneath); incoming videos
 * hard-cut with no overlap so their first frame is never blended.
 */
export function SignageCrossfade({
  item,
  className,
  surface,
  muted = true,
  setVideoRef,
  onImageError,
}: {
  item: DeviceMediaItem;
  /** Positioning + fit classes applied to every media layer. */
  className: string;
  /** Test-id suffix: image-/video-`surface` (outgoing layer adds -outgoing). */
  surface: "signage" | "foreground";
  muted?: boolean;
  setVideoRef?: (el: HTMLVideoElement | null) => void;
  /** Called when the CURRENT still image fails to load (broken/expired URL). */
  onImageError?: () => void;
}) {
  const [layers, setLayers] = useState<Layers>({
    current: item,
    outgoing: null,
    fade: false,
  });

  useEffect(() => {
    setLayers((prev) => {
      if (item.id === prev.current.id) {
        return item === prev.current ? prev : { ...prev, current: item };
      }
      const fade = item.type === "image";
      return { current: item, outgoing: fade ? prev.current : null, fade };
    });
  }, [item]);

  useEffect(() => {
    if (!layers.outgoing) return;
    const timer = setTimeout(
      () => setLayers((prev) => ({ ...prev, outgoing: null })),
      FADE_MS + OUTGOING_LINGER_MS,
    );
    return () => clearTimeout(timer);
  }, [layers.outgoing]);

  // Layers are keyed by item id so an item demoted to outgoing keeps its DOM
  // media element (a playing video must not restart mid-fade). The incoming
  // layer renders after the outgoing one, so it stacks on top.
  return (
    <>
      {layers.outgoing && (
        <MediaLayer
          key={layers.outgoing.id}
          item={layers.outgoing}
          className={className}
          surface={surface}
          outgoing
          fadeIn={false}
          muted
        />
      )}
      <MediaLayer
        key={layers.current.id}
        item={layers.current}
        className={className}
        surface={surface}
        outgoing={false}
        fadeIn={layers.fade}
        muted={muted}
        setVideoRef={setVideoRef}
        onImageError={onImageError}
      />
    </>
  );
}

function MediaLayer({
  item,
  className,
  surface,
  outgoing,
  fadeIn,
  muted,
  setVideoRef,
  onImageError,
}: {
  item: DeviceMediaItem;
  className: string;
  surface: string;
  outgoing: boolean;
  fadeIn: boolean;
  muted: boolean;
  setVideoRef?: (el: HTMLVideoElement | null) => void;
  onImageError?: () => void;
}) {
  // Incoming images mount transparent, then flip opaque a frame later so the
  // CSS opacity transition actually runs (double rAF outlives style flush).
  const [visible, setVisible] = useState(!fadeIn);
  useEffect(() => {
    if (!fadeIn) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [fadeIn]);

  const fadeClass = fadeIn
    ? ` transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`
    : "";
  const testId = `${item.type === "image" ? "image" : "video"}-${surface}${
    outgoing ? "-outgoing" : ""
  }`;

  if (item.type === "image") {
    return (
      <img
        src={item.url}
        alt={item.title}
        className={className + fadeClass}
        data-testid={testId}
        // Broken/expired still URL: hand off to the screen failure budget so
        // the player skips past it instead of freezing on a broken glyph
        // (CB-122). Only the current (non-outgoing) layer reports.
        onError={outgoing ? undefined : onImageError}
      />
    );
  }
  return (
    <video
      ref={outgoing ? undefined : setVideoRef}
      src={item.url}
      autoPlay
      muted={muted}
      playsInline
      className={className + fadeClass}
      data-testid={testId}
    />
  );
}
